import type React from "react";
import { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import {
  getGptConversationsSortedByRecency,
  getGptConversationsPagination,
  getActiveGptConversation,
  fetchMoreGptConversations,
  updateGptConversationName,
  setActiveGptConversation,
} from "../state/chatbotSlice";
import { useTranslation } from "react-i18next";
import { FaRegEdit } from "react-icons/fa";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import Spinner from "src/common/components/Loader/Spinner";
import fromTimestampToPrettyFrenchDate from "src/common/util/fromTimestampToPrettyFrenchDate";
import GptConversationThreeDotButton from "./GptConversationThreeDotButton";

interface GptConversationLoaderProps {
  onConversationSelect: (conversationId: number) => void;
  onResetChat: () => void;
}

export const GPT_CONVERSATION_LOADER_COMPONENT_ID = "SELECT_CHAT_HISTORY_COMPONENT";

const GptConversationLoader: React.FC<GptConversationLoaderProps> = ({
  onConversationSelect,
  onResetChat,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userPreviousGptConversations = useAppSelector(getGptConversationsSortedByRecency);
  const pagination = useAppSelector(getGptConversationsPagination);
  const activeConversation = useAppSelector(getActiveGptConversation);

  const [editingConversationId, setEditingConversationId] = useState<number | null>(null);
  const [newConversationName, setNewConversationName] = useState<string>("");

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null); // Ref to the scrollable container

  const handleConversationSelect = (conversationId: number) => {
    if (editingConversationId === conversationId) return;
    const selectedConversation = userPreviousGptConversations.find(
      (conv) => conv.id === conversationId,
    );
    if (selectedConversation) {
      dispatch(setActiveGptConversation(selectedConversation));
      onConversationSelect(conversationId);
    }
  };

  const handleRename = (id: number) => {
    const conversation = userPreviousGptConversations.find((conv) => conv.id === id);
    if (conversation) {
      const fallbackName = fromTimestampToPrettyFrenchDate(
        conversation.conversation_metadata.timestamp,
      );
      setEditingConversationId(id);
      setNewConversationName(conversation.name || fallbackName);
    }
  };

  const handleRenameSubmit = (id: number) => {
    if (newConversationName.trim() !== "") {
      void dispatch(updateGptConversationName({ id, newName: newConversationName }));
      setEditingConversationId(null);
    }
  };

  const handleRenameCancel = () => {
    setEditingConversationId(null);
  };

  const handleThreeDotMenuOpen = (conversationId: number) => {
    const lastItemIndex = userPreviousGptConversations.length - 1;
    const isLastItem = userPreviousGptConversations[lastItemIndex]?.id === conversationId;

    if (isLastItem && scrollableRef.current) {
      const container = scrollableRef.current;

      // Check if the scrollbar exists
      const isScrollable = container.scrollHeight > container.clientHeight;

      if (isScrollable) {
        // Scroll to the bottom of the container
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  useEffect(() => {
    if (inputRef.current && editingConversationId !== null) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingConversationId]);

  const handleScroll = () => {
    const el = scrollableRef.current;
    if (!el || !pagination.hasMore || pagination.isLoadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      void dispatch(fetchMoreGptConversations({ start: userPreviousGptConversations.length }));
    }
  };

  return (
    <div className="w-full max-h-[80vh] h-[80vh] p-1 bg-gray-100">
      <button
        className="w-full bg-white p-2 mb-1 rounded-lg shadow hover:bg-green-500 hover:text-white border transition-colors"
        onClick={onResetChat}
      >
        <div className="flex items-center justify-center font-bold p-2">
          <FaRegEdit className="mr-2" />
          {t("pages.chat.new")}
        </div>
      </button>
      <IsLoading
        componentId={GPT_CONVERSATION_LOADER_COMPONENT_ID}
        showSuccess={false}
        spinnerPlaceholder
      >
        <div
          ref={scrollableRef}
          onScroll={handleScroll}
          className="space-y-2 overflow-hidden overflow-y-auto h-[93%]"
        >
          {userPreviousGptConversations.map((conversation, index) => {
            const id = conversation.id;
            if (id === undefined) return null;
            const isEditing = editingConversationId === id;

            const name =
              conversation?.name ??
              fromTimestampToPrettyFrenchDate(conversation.conversation_metadata.timestamp);

            const isActive = activeConversation?.id === id;

            return (
              <div key={id} className="relative group">
                {isEditing ? (
                  <input
                    ref={inputRef}
                    className="w-full bg-white text-sm p-2 rounded-lg shadow pr-10 text-left"
                    value={newConversationName}
                    onChange={(e) => setNewConversationName(e.target.value)}
                    onBlur={() => handleRenameSubmit(id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRenameSubmit(id);
                      if (e.key === "Escape") handleRenameCancel();
                    }}
                    autoFocus
                  />
                ) : (
                  <button
                    className={`w-full text-sm p-2 rounded-lg shadow transition-colors pr-10 text-left h-[44px] flex items-center ${
                      isActive ? "bg-green-600 text-white" : "bg-white hover:bg-green-100"
                    }`}
                    onClick={() => handleConversationSelect(id)}
                  >
                    <span className="line-clamp-2 leading-tight">{name}</span>
                  </button>
                )}
                <div className="absolute top-1 right-1 h-full flex items-center">
                  <GptConversationThreeDotButton
                    id={id}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onRename={() => handleRename(id)}
                    onMenuOpen={() => handleThreeDotMenuOpen(id)}
                    menuPosition={index <= 3 ? "below" : "above"} // so the dropdown always appear when it should 🐵
                  />
                </div>
              </div>
            );
          })}
          {pagination.isLoadingMore && (
            <div className="flex justify-center py-2">
              <Spinner relative style={{ width: 24, height: 24 }} />
            </div>
          )}
          {pagination.hasMore && <div className="h-1" />}
        </div>
      </IsLoading>
    </div>
  );
};

export default GptConversationLoader;
